import { Button, Center, Divider, Flex, Link, Spinner, Stack, Text } from '@chakra-ui/react';
import { Navigate, Link as RouterLink, useParams } from 'react-router-dom';

import LoginForm from '@/client/components/forms/authentication/LoginForm';
import { Partner } from '../types/Partner';
import { useConfigStore } from '@/client/services/state/configStore';
import useDocumentTitle from '../utils/useDocumentTitle';
import { useEffect } from 'react';
import { useGetAlternativeLogins } from '../services/hooks/preauth/useGetAlternativeLogins';
import { usePartnerStore } from '../services/state/partnerStore';
import { useTranslation } from 'react-i18next';

export default function Login() {
  useDocumentTitle('Sign In');

  const { config } = useConfigStore();
  const { t } = useTranslation();
  const { partner: partnerId } = useParams<{
    partner?: string;
  }>();
  const { partner, setPartner } = usePartnerStore();
  const { isLoading, isError, data, error } = useGetAlternativeLogins(partnerId);

  const renderRegisterLink = config.registerAllow || config.features.enable_registration_forms;

  const registerLink = config.registerAllow ? '/register' : '/registration';

  useEffect(() => {
    setPartner(data?.partner || ({} as Partner));
  }, [data?.partner]);

  if (isLoading)
    return (
      <Center marginTop="25%">
        <Spinner />
      </Center>
    );

  if (isError)
    return (
      <div>
        Error: {error.message}: {error.response?.data?.message?.toString()}
      </div>
    );

  if (data.suspended) return <Navigate to="/suspended" replace />;

  if (data.partner && !partner._id) {
    return <div>waiting for partner to load</div>;
  }

  const ssoLogin = () => {
    let { link } = data;
    if (data.use_options) {
      link = '/saml/login';
    }

    if (config.signinUseNewWindow) {
      // ! Important to set child name as it is expected in the preauth-handler
      window.open(link, 'childsso', 'target=_blank');
    } else {
      window.location.href = link;
    }
  };

  const partnerLoginConfig = {
    checkForPartnerMatch: true,
    partner: partner?._id,
  };

  if (partner?._id && !partner.active) {
    return (
      <Flex alignItems="center" justifyContent="center" whiteSpace="nowrap" marginBottom={4}>
        <Text fontSize="sm" marginLeft={20} marginRight={20}>
          {t('preauth.signin.extendAccountInactive')}
        </Text>
      </Flex>
    );
  }

  return (
    <Stack spacing={6}>
      <Stack spacing={0}>
        <Text fontSize={{ base: '24px', md: '36px' }} fontWeight={700}>
          {t('login.welcome')}
        </Text>

        {(renderRegisterLink || data.signin_userid) && (
          <Text fontSize="14px" fontWeight={500} color="neutral.800">
            {t('login.noAccount')}{' '}
            {renderRegisterLink && (
              <Link as={RouterLink} to={registerLink} variant="neutral">
                {t('login.registerNow')}
              </Link>
            )}{' '}
            {renderRegisterLink && data.signin_userid && 'or '}
            {data.signin_userid && (
              <Link as={RouterLink} to="/register-with-userid" variant="neutral">
                {data.signin_userid_copy.register_button ||
                  t('preauth.signin.register_with_userid')}
              </Link>
            )}
          </Text>
        )}
      </Stack>

      <LoginForm
        userIdPattern={data.signin_userid_pattern}
        userIdLimit={data.signin_userid_limit}
        partnerLoginConfig={partnerLoginConfig}
      />

      {data.status && data.link && (
        <>
          <Divider variant="neutral" />

          <Button
            backgroundColor="neutral.50"
            fontSize="16px"
            fontWeight={600}
            color="baseBlack"
            border="1px solid"
            borderColor="neutral.100"
            _hover={{ backgroundColor: 'neutral.100' }}
            onClick={ssoLogin}
          >
            {data.sso_copy?.sso_button ?? 'Log in with SSO'}
          </Button>
        </>
      )}
    </Stack>
  );
}
